import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Image, type ImageProps } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn, mergeRefs } from '@opendoor/atoms/utils';
import { IconPhoto } from '@tabler/icons-react';
import { useIsomorphicLayoutEffect } from 'components/shared/hooks/useIsomorphicLayoutEffect';

export type PropertyPhotoLoadState = 'loading' | 'loaded' | 'unavailable';

export const usePropertyPhotoLoadState = (sourceKey: string | null) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [loadedSourceKey, setLoadedSourceKey] = useState<string | null>(null);
  const [failedSourceKey, setFailedSourceKey] = useState<string | null>(null);

  useIsomorphicLayoutEffect(() => {
    const image = imageRef.current;
    if (!sourceKey || !image?.complete) {
      return;
    }

    if (image.naturalWidth > 0) {
      setLoadedSourceKey(sourceKey);
      setFailedSourceKey(null);
    } else {
      setFailedSourceKey(sourceKey);
    }
  }, [sourceKey]);

  let state: PropertyPhotoLoadState = 'loading';
  if (sourceKey == null || failedSourceKey === sourceKey) {
    state = 'unavailable';
  } else if (loadedSourceKey === sourceKey) {
    state = 'loaded';
  }

  const onLoad = useCallback(() => {
    if (sourceKey) {
      setLoadedSourceKey(sourceKey);
      setFailedSourceKey(null);
    }
  }, [sourceKey]);

  const onError = useCallback(() => {
    if (sourceKey) {
      setFailedSourceKey(sourceKey);
    }
  }, [sourceKey]);

  return {
    imageRef,
    state,
    onLoad,
    onError,
  };
};

export const PropertyPhotoPlaceholder = ({
  state,
  className,
  label,
}: {
  state: Exclude<PropertyPhotoLoadState, 'loaded'>;
  className?: string;
  /** Explains the empty state, e.g. "Photos coming soon" for pre-listing homes. */
  label?: string;
}) => (
  <Box
    // A labeled placeholder is content once it actually shows the label — the
    // icon stays hidden, and the loading state stays decorative.
    aria-hidden={label && state === 'unavailable' ? undefined : true}
    data-property-photo-state={state}
    className={cn(
      'items-center justify-center bg-od-bg-secondary',
      state === 'loading' && 'skeleton',
      className,
    )}
  >
    {state === 'unavailable' && (
      <Col className="items-center gap-2">
        <IconPhoto size={32} className="text-od-content-inactive-secondary" aria-hidden />
        {label ? (
          <Text as="span" className="text-body-sm text-od-content-tertiary">
            {label}
          </Text>
        ) : null}
      </Col>
    )}
  </Box>
);

export type PropertyPhotoImageProps = Omit<ImageProps, 'src'> & {
  src?: string | null;
  isPending?: boolean;
  containerClassName?: string;
  placeholderClassName?: string;
  /** Explains the no-photo state, e.g. "Photos coming soon" for pre-listing homes. */
  placeholderLabel?: string;
};

export const PropertyPhotoImage = React.forwardRef<HTMLImageElement, PropertyPhotoImageProps>(
  (
    {
      src,
      isPending = false,
      containerClassName,
      placeholderClassName,
      placeholderLabel,
      className,
      onLoad,
      onError,
      ...imageProps
    },
    forwardedRef,
  ) => {
    const sourceKey = src || null;
    const {
      imageRef: loadStateRef,
      state,
      onLoad: markLoaded,
      onError: markUnavailable,
    } = usePropertyPhotoLoadState(sourceKey);
    const imageRef = useMemo(
      () => mergeRefs(loadStateRef, forwardedRef),
      [forwardedRef, loadStateRef],
    );
    const placeholderState = sourceKey == null && isPending ? 'loading' : state;
    const handleLoad = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        markLoaded();
        onLoad?.(event);
      },
      [markLoaded, onLoad],
    );
    const handleError = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        markUnavailable();
        onError?.(event);
      },
      [markUnavailable, onError],
    );

    return (
      <Box
        data-property-photo-state={placeholderState === 'loading' ? 'loading' : undefined}
        className={cn(
          'relative overflow-hidden',
          placeholderState === 'loading' && ['bg-od-bg-secondary skeleton', placeholderClassName],
          containerClassName,
        )}
      >
        {sourceKey && (
          <Image
            {...imageProps}
            ref={imageRef}
            src={sourceKey}
            className={className}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
        {placeholderState === 'unavailable' && (
          <PropertyPhotoPlaceholder
            state={placeholderState}
            className={cn('absolute inset-0', placeholderClassName)}
            label={placeholderLabel}
          />
        )}
      </Box>
    );
  },
);

PropertyPhotoImage.displayName = 'PropertyPhotoImage';
